import * as React from 'react'

import { apiFetch, apiJson } from '@/lib/api'
import type { Asset, AssetFolder } from '@/types'

interface FolderListPayload {
  items: AssetFolder[]
  total: number
}

interface AssetListPayload {
  items: Asset[]
  total: number
}

export function useFolders() {
  const [folders, setFolders] = React.useState<AssetFolder[]>([])
  const [loading, setLoading] = React.useState(true)
  const [error, setError] = React.useState<string | null>(null)

  const load = React.useCallback(async () => {
    try {
      const data = await apiJson<FolderListPayload>('/api/v1/folders')
      setFolders(data.items)
      setError(null)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setLoading(false)
    }
  }, [])

  React.useEffect(() => {
    load()
  }, [load])

  return { folders, loading, error, refresh: load }
}

export function useAssets(folderId: number | null) {
  const [assets, setAssets] = React.useState<Asset[]>([])
  const [loading, setLoading] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const load = React.useCallback(async () => {
    if (folderId == null) {
      setAssets([])
      return
    }
    setLoading(true)
    try {
      const data = await apiJson<AssetListPayload>(
        `/api/v1/folders/${folderId}/assets?limit=200`
      )
      setAssets(data.items)
      setError(null)
    } catch (err) {
      setError((err as Error).message)
    } finally {
      setLoading(false)
    }
  }, [folderId])

  React.useEffect(() => {
    load()
  }, [load])

  return { assets, loading, error, refresh: load }
}

export function useUploadAsset() {
  const [uploading, setUploading] = React.useState(false)

  const upload = React.useCallback(
    async (folderId: number, file: File): Promise<Asset> => {
      setUploading(true)
      try {
        const formData = new FormData()
        formData.append('file', file)

        const response = await apiFetch(`/api/v1/folders/${folderId}/assets`, {
          method: 'POST',
          body: formData,
        })

        if (!response.ok) {
          const data = await response.json().catch(() => ({}))
          throw new Error(data.detail || `Upload failed (${response.status})`)
        }

        return (await response.json()) as Asset
      } finally {
        setUploading(false)
      }
    },
    []
  )

  return { upload, uploading }
}
